import { useEffect, useMemo, useState } from "react";
import { X, Lock, Loader2, CreditCard, ShieldCheck, AlertCircle, BadgeCheck } from "lucide-react";

const FX_FALLBACK = 1580; // NGN per USD — used if live API fails

// Format NGN with thousands separators
const fmtNGN = (n) =>
  `₦${Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
const fmtUSD = (n) => `$${Number(n).toLocaleString()}`;

export default function FlutterwavePaymentModal({ open, onClose, selectedFunders = [], quote }) {
  const [rate, setRate] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(false);

  // Card form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [card, setCard] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");

  const [status, setStatus] = useState("idle"); // idle | processing | declined

  // Fetch live USD → NGN rate when modal opens
  useEffect(() => {
    if (!open || rate !== null) return;
    let cancelled = false;
    setFxLoading(true);
    setFxError(false);
    fetch("https://open.er-api.com/v6/latest/USD")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const ngn = data?.rates?.NGN;
        if (ngn && Number.isFinite(ngn)) setRate(ngn);
        else {
          setRate(FX_FALLBACK);
          setFxError(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setRate(FX_FALLBACK);
        setFxError(true);
      })
      .finally(() => !cancelled && setFxLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, rate]);

  // Reset transient state when closing
  useEffect(() => {
    if (!open) {
      setStatus("idle");
    }
  }, [open]);

  const totalUSD = quote?.total ?? 0;
  const totalNGN = useMemo(() => (rate ? totalUSD * rate : 0), [rate, totalUSD]);

  const onPay = (e) => {
    e?.preventDefault();
    if (status === "processing") return;
    setStatus("processing");
    // simulate processing then decline
    setTimeout(() => setStatus("declined"), 2200);
  };

  const formatCardNumber = (v) =>
    v
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(.{4})/g, "$1 ")
      .trim();

  const formatExpiry = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 4);
    if (digits.length < 3) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  if (!open) return null;

  return (
    <div
      data-testid="payment-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Flutterwave-style orange header */}
        <header className="relative bg-[#F5A623] px-6 pt-6 pb-7 text-white">
          <button
            data-testid="payment-close"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-white/80 hover:bg-white/15"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Brand mark */}
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-md bg-white text-[#F5A623]">
              <span className="text-xs font-black">FW</span>
            </div>
            <span className="text-sm font-semibold tracking-wide">Flutterwave</span>
          </div>

          {/* Merchant + amount */}
          <div className="mt-5 flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/75">
                Pay SeedTechNG
              </div>
              <div className="mt-0.5 text-xs text-white/70">hello@seeds.com3.ng</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-white/70">Amount</div>
              <div className="text-2xl font-bold leading-tight">
                {fxLoading || rate === null ? "—" : fmtNGN(totalNGN)}
              </div>
              <div className="text-[11px] text-white/80">≈ {fmtUSD(totalUSD)}</div>
            </div>
          </div>
        </header>

        {/* FX disclaimer */}
        <div className="flex items-center justify-between border-b border-neutral-200 bg-orange-50/60 px-6 py-2.5 text-[11px] text-neutral-600">
          <span>
            FX rate:{" "}
            <strong className="text-neutral-800">
              1 USD = {rate ? Math.round(rate * 100) / 100 : "—"} NGN
            </strong>
          </span>
          <span className="text-neutral-500">{fxError ? "Cached rate" : "Live mid-market"}</span>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {status === "declined" ? (
            <DeclinedState
              onTryAgain={() => setStatus("idle")}
              onClose={onClose}
              totalNGN={totalNGN}
              fxLoading={fxLoading || rate === null}
            />
          ) : (
            <form onSubmit={onPay} className="space-y-4">
              {/* Funders summary */}
              {selectedFunders.length > 0 && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-xs text-neutral-600">
                  <div className="mb-1 font-medium text-neutral-800">
                    {selectedFunders.length} funder
                    {selectedFunders.length > 1 ? "s" : ""} in this order:
                  </div>
                  <div className="line-clamp-2 leading-relaxed">
                    {selectedFunders.map((f) => f.name).join(" · ")}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-neutral-700">Email</label>
                <input
                  data-testid="pay-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm focus:border-[#F5A623] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/20"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-neutral-700">Cardholder name</label>
                <input
                  data-testid="pay-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="As shown on card"
                  className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm focus:border-[#F5A623] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/20"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-neutral-700">Card number</label>
                <div className="relative mt-1">
                  <CreditCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    data-testid="pay-card"
                    type="text"
                    inputMode="numeric"
                    required
                    value={card}
                    onChange={(e) => setCard(formatCardNumber(e.target.value))}
                    placeholder="0000 0000 0000 0000"
                    className="w-full rounded-md border border-neutral-300 bg-white py-2.5 pl-9 pr-3 text-sm tracking-widest focus:border-[#F5A623] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-neutral-700">Expiry</label>
                  <input
                    data-testid="pay-expiry"
                    type="text"
                    inputMode="numeric"
                    required
                    value={expiry}
                    onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    placeholder="MM / YY"
                    className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm focus:border-[#F5A623] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/20"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-700">CVV</label>
                  <input
                    data-testid="pay-cvv"
                    type="text"
                    inputMode="numeric"
                    required
                    maxLength={4}
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="123"
                    className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm focus:border-[#F5A623] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/20"
                  />
                </div>
              </div>

              <button
                data-testid="pay-submit"
                type="submit"
                disabled={status === "processing" || fxLoading || rate === null}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#F5A623] py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#E69412] disabled:opacity-60"
              >
                {status === "processing" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Processing…
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Pay {fxLoading || rate === null ? "—" : fmtNGN(totalNGN)}
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-500">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                Secured by Flutterwave · 256-bit encryption
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function DeclinedState({ onTryAgain, onClose, totalNGN, fxLoading }) {
  return (
    <div data-testid="payment-declined" className="text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
        <AlertCircle className="h-7 w-7 text-red-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-neutral-900">Payment declined</h3>
      <p className="mx-auto mt-1 max-w-xs text-sm text-neutral-600">
        Your bank declined this transaction (response code{" "}
        <span className="font-mono text-neutral-800">05 — Do Not Honor</span>). Please try a
        different card or contact your card issuer.
      </p>

      <dl className="mx-auto mt-5 max-w-xs rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-left text-xs">
        <div className="flex justify-between text-neutral-600">
          <dt>Amount</dt>
          <dd className="font-medium text-neutral-900">
            {fxLoading ? "—" : fmtNGN(totalNGN)}
          </dd>
        </div>
        <div className="mt-1 flex justify-between text-neutral-600">
          <dt>Merchant</dt>
          <dd className="text-neutral-900">SeedTechNG</dd>
        </div>
        <div className="mt-1 flex justify-between text-neutral-600">
          <dt>Reference</dt>
          <dd className="font-mono text-neutral-900">
            FLW-{Math.random().toString(36).slice(2, 10).toUpperCase()}
          </dd>
        </div>
      </dl>

      <div className="mt-5 flex gap-2">
        <button
          data-testid="pay-try-again"
          onClick={onTryAgain}
          className="flex-1 rounded-md bg-[#F5A623] py-2.5 text-sm font-semibold text-white hover:bg-[#E69412]"
        >
          Try again
        </button>
        <button
          data-testid="pay-cancel"
          onClick={onClose}
          className="flex-1 rounded-md border border-neutral-300 bg-white py-2.5 text-sm font-medium text-neutral-700 hover:border-neutral-500"
        >
          Cancel
        </button>
      </div>

      <p className="mt-4 text-[11px] text-neutral-500">
        <BadgeCheck className="-mt-0.5 mr-1 inline h-3 w-3 text-emerald-600" />
        Need help? Email{" "}
        <a className="text-neutral-700 underline" href="mailto:hello@seeds.com3.ng">
          hello@seeds.com3.ng
        </a>
      </p>
    </div>
  );
}
